import * as React from 'react';
import * as i18next from 'i18next';
import {reactI18nextModule, Trans, TranslationFunction} from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {LazyLoader} from './LazyLoader';
import {TransProps} from 'react-i18next/src/trans';
import Pseudo from 'i18next-pseudo';
import configString from '../utility/Config';
import {observer} from 'mobx-react';

export type MessageProvider = TranslationFunction;

let globalInstance: i18next.i18n | null = null;

export const createInstance = (): Promise<i18next.i18n> => {
    if (globalInstance !== null) {
        throw new Error('I18N already initialized');
    }

    const i18n = i18next
        .use(reactI18nextModule)
        .use(LanguageDetector)
        .use(new Pseudo({languageToPseudo: 'de-DE'}))
        .use(LazyLoader);
    const initializedI18n = new Promise<i18next.i18n>((resolve) => {
        i18n.on('initialized', () => {
            globalInstance = i18n;
            resolve(i18n);
        });
    });
    const postProcessors: string[] = [];

    if (configString('TEXT_DEBUG') !== '') {
        console.log('Using i18n-pseudo');
        postProcessors.push('pseudo');
    }

    i18n.init({
        fallbackLng: 'en',
        debug: configString('TEXT_DEBUG') !== '',
        defaultNS: 'common',
        ns: ['common', 'dashboard', 'kpi', 'chart', 'table', 'insights', 'error', 'report', 'abtest'],
        keySeparator: '.',
        nsSeparator: ':',
        postProcess: postProcessors,
        react: {
            wait: false,
        },
    });
    return initializedI18n;
};

export const setLanguage = (lang: string) => {
    window.localStorage.setItem('i18nextLng', lang);
};

export const getLanguage = (): string | undefined => {
    return (globalInstance && globalInstance.language) || undefined;
};

/**
 * Global access to translations. Only use this method when there is no other way.
 *
 * Use these instead:
 * @see I18n
 * @see TProvider
 */
export const t: TranslationFunction = (key, options) => {
    if (globalInstance === null) {
        throw new Error('not initialized yet');
    }
    return globalInstance.t(key, options);
};

/**
 * See I18n.md
 */
export const I18n: React.FunctionComponent<TransProps> = (props: TransProps) => {
    return <Trans {...props} />;
};

/**
 * See I18n.md
 */
export const TProvider: React.FunctionComponent<{children: (t: TranslationFunction) => React.ReactNode}> = observer(({children}) => {
    return <React.Fragment>{children(t)}</React.Fragment>;
});



// WEBPACK FOOTER //
// ./src/i18n/i18n.tsx